import { IGirafeContext } from '@geogirafe/lib-geoportal/core';
import { MultiPrintResponse, MultiPrintStatus, ReportServiceConfig } from '../../bs/types';

export default class BsPrintingService {
  private context: IGirafeContext;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private multiPrintInfo: MultiPrintResponse | null = null;
  private reportPromiseReject!: (reason?: unknown) => void;
  private reportPromiseResolve!: (value: string) => void;

  constructor(context: IGirafeContext) {
    this.context = context;
  }

  protected get config() {
    return this.context.configManager.Config;
  }

  async createMultiReport(layout: string, param1: string, param2: string | null = null): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.reportPromiseResolve = resolve;
      this.reportPromiseReject = reject;

      const multiUrl = `${this.getReportBaseUrl}multireport/${layout}/${param1}/${param2}`;

      const requestOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'text/xml; charset=UTF-8' },
        body: JSON.stringify({}),
        credentials: 'include'
      };

      fetch(multiUrl, requestOptions)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`MultiReport creation failed: ${response.statusText}`);
          }
          this.multiPrintInfo = await response.json();
          this.getMultiPrintStatus();
        })
        .catch((err) => {
          this.reportPromiseReject(err);
        });
    });
  }

  protected get getReportBaseUrl() {
    return (this.context.configManager.Config.extendedConfig!.reportService as ReportServiceConfig).baseUrl;
  }

  async getMultiPrintStatus() {
    if (!this.multiPrintInfo) {
      this.reportPromiseReject('No multiPrintInfo available');
      return;
    }
    const url = `${this.getReportBaseUrl}${this.multiPrintInfo.statusUrl}`;

    fetch(url)
      .then(async (resp) => {
        if (!resp.ok) {
          throw new Error(`getMultiPrintStatus failed: ${resp.statusText}`);
        }
        const statusData = (await resp.json()) as MultiPrintStatus;
        this.handleMultiStatusSuccess(statusData);
      })
      .catch((error) => {
        this.handleMultiStatusError(error);
      });
  }

  async handleMultiStatusSuccess(resp: MultiPrintStatus) {
    if (!this.multiPrintInfo) {
      this.reportPromiseReject('multiPrintInfo is null');
      return;
    }

    if (resp.alldone) {
      for (const pr of resp.prints) {
        if (pr.status === 'error') {
          this.reportPromiseReject(pr.error || 'Unknown multi print error');
          return;
        }
      }
      const downloadurl = `${this.getReportBaseUrl}${this.multiPrintInfo.downloadUrl}`;
      this.reportPromiseResolve(downloadurl);
    } else {
      this.pollTimer = setTimeout(() => {
        this.getMultiPrintStatus();
      }, 1000);
    }
  }

  private handleMultiStatusError(error: unknown) {
    console.error('handleMultiStatusError:', error);
    this.reportPromiseReject('Fehler beim Erstellen des Dokumentes');
  }

  cancelMultiPrint() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
